/**
 * MetricDistributionRowView - Distribution dot row with shell-hosted dropdown
 *
 * Displays a metric label with score and a distribution dot plot.
 * SE-5568: the metric selector renders through `FixedPositionDropdownShell`
 * (open-state + positioning owned by the Container), replacing the gluestack
 * Menu + RN Modal workaround.
 * Supports hover tooltips on distribution dots (web only).
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React from "react";
import { Platform } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import {
  renderMetricShellDropdown,
  renderMetricShellOption,
} from "@/features/player-detail/utils/metricShellDropdown";
import type { ShellDropdownWiring } from "@/features/player-detail/utils/performanceAnalyticsViewHelpers";

import type {
  BeeSwarmInput,
  DotPosition,
} from "@/features/player-detail/utils/beeSwarmPositioning";
import type { MetricOption } from "@/features/player-detail/types";

/**
 * Metric data for distribution charts with player info
 */
interface MetricData {
  readonly label: string;
  readonly value: number | null;
  readonly distributionData: readonly BeeSwarmInput[];
}

/**
 * Props for MetricDistributionRowView component
 */
interface MetricDistributionRowViewProps {
  /** Metric data */
  readonly metric: MetricData;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Available metrics for selection */
  readonly availableMetrics: readonly MetricOption[];
  /** Shell open-state + positioning wiring from the Container (SE-5568) */
  readonly dropdown: ShellDropdownWiring;
  /** Shell-hosted trigger element from the Container */
  readonly trigger: React.JSX.Element;
  /** Factory for creating stable metric select handlers */
  readonly createSelectHandler: (metric: MetricOption) => () => void;
  /** Optional comparison player value (shows orange dot when provided) */
  readonly comparisonValue?: number | null;
  /** Bee swarm positions for distribution dots (prevents overlap) */
  readonly swarmPositions: readonly DotPosition[];
  /** Maximum value for axis scaling (default: 100) */
  readonly maxValue?: number;
  /** Reduces dot row height for mobile carousel (h-14 instead of h-20) */
  readonly compact?: boolean;
}

/** Player dot highlight color */
const PLAYER_DOT_COLOR = "#22d3ee";

/** Comparison player dot color (orange) */
const COMPARISON_DOT_COLOR = "#f97316";

/** Minimum edge padding for player dots (percentage) */
const DOT_EDGE_PADDING_PCT = 2;

/**
 * Clamp a percentage value to stay within bounds with edge padding
 * Replaces CSS clamp() which isn't supported in React Native
 * @param value - Percentage value (0-100)
 * @returns Clamped percentage value (2-98)
 */
const clampDotPosition = (value: number): number =>
  Math.max(DOT_EDGE_PADDING_PCT, Math.min(100 - DOT_EDGE_PADDING_PCT, value));

/**
 * Get dot color based on theme
 * @param isDark - Whether dark mode is active
 * @returns Hex color for distribution dots
 */
const getDotColor = (isDark: boolean): string =>
  isDark ? "#6b7280" : "#9ca3af";

/**
 * Axis line class (static — semantic tokens auto-invert by theme)
 */
const AXIS_LINE_CLASS = "absolute inset-x-0 top-1/2 h-px bg-surface-muted";

/**
 * Distribution (gray) dot style (consumed by a gluestack ui Box).
 * design-system: dynamic-style waiver — data-driven value (bee-swarm position, theme color)
 * @param leftPct - Horizontal position as a clamped percentage
 * @param top - Vertical offset in pixels (swarm spread)
 * @param isDark - Whether dark mode is active
 * @param hasTooltip - Whether the dot shows a hover tooltip (web pointer cursor)
 */
const baseDotStyle = (
  leftPct: number,
  top: number,
  isDark: boolean,
  hasTooltip: boolean
) => ({
  left: `${leftPct}%`,
  top,
  backgroundColor: getDotColor(isDark),
  width: 6,
  height: 6,
  marginLeft: -3,
  marginTop: -3,
  cursor: hasTooltip ? "pointer" : "default",
});

/**
 * Hover tooltip arrow style — CSS triangle (consumed by a gluestack ui Box).
 * design-system: dynamic-style waiver — measured geometry + theme color
 * @param isDark - Whether dark mode is active
 */
const tooltipArrowStyle = (isDark: boolean) => ({
  width: 0,
  height: 0,
  borderLeft: "5px solid transparent",
  borderRight: "5px solid transparent",
  borderTop: isDark ? "5px solid #18181b" : "5px solid #ffffff",
});

/**
 * Highlighted marker dot style — player/comparison (consumed by a gluestack ui Box).
 * design-system: dynamic-style waiver — data-driven value (measured position)
 * @param color - Dot fill color
 * @param leftPct - Horizontal position as a clamped percentage
 */
const markerDotStyle = (color: string, leftPct: number) => ({
  left: `${leftPct}%`,
  backgroundColor: color,
  width: 10,
  height: 10,
  marginLeft: -5,
  top: "50%",
  marginTop: -5,
  zIndex: 10,
});

/**
 * MetricDistributionRowView displays a metric with a shell-hosted dropdown and distribution dots
 * @param props - Component props
 * @param props.metric - Metric data
 * @param props.isDark - Whether dark mode is active
 * @param props.availableMetrics - Available metrics for selection
 * @param props.dropdown - Shell open-state + positioning wiring (SE-5568)
 * @param props.trigger - Shell-hosted trigger element
 * @param props.createSelectHandler - Factory for stable metric select handlers
 * @param props.comparisonValue - Optional comparison player value (shows orange dot)
 * @param props.swarmPositions - Bee swarm positions for distribution dots (prevents overlap)
 * @param props.maxValue - Maximum value for axis scaling
 * @param props.compact - Reduces dot row height for mobile carousel
 * @returns VStack containing the metric row
 */
const MetricDistributionRowView = ({
  metric,
  isDark,
  availableMetrics,
  dropdown,
  trigger,
  createSelectHandler,
  comparisonValue,
  swarmPositions,
  maxValue = 100,
  compact = false,
}: MetricDistributionRowViewProps) => (
  <Stack className="relative z-0 gap-2 hover:z-50">
    <Stack direction="horizontal" className="relative items-center gap-3">
      {renderMetricShellDropdown({
        wiring: dropdown,
        trigger,
        options: availableMetrics.map(option =>
          renderMetricShellOption({
            label: option.label,
            value: option.value,
            isSelected: option.label === metric.label,
            onPress: createSelectHandler(option),
            isDark,
          })
        ),
      })}
    </Stack>
    <Stack className="gap-1">
      {/* Dots and axis line container - tall enough for symmetric swarm spread (28px each direction) */}
      {/* overflow-visible so hover tooltips above dots are not clipped (SE-3854) */}
      <Stack
        direction="horizontal"
        className={`relative ${compact ? "h-14" : "h-20"} items-center px-1`}
      >
        {/* Axis line */}
        <Box className={AXIS_LINE_CLASS} />
        {/* Distribution dots — overflow-visible so tooltips render above the row */}
        <Box className="relative flex size-full flex-row items-center">
          {/* Render distribution dots (gray) with bee swarm positioning */}
          {swarmPositions.map(pos => {
            const hasTooltip = Boolean(pos.playerName) && Platform.OS === "web";
            return (
              <Box
                key={`${metric.label}-${pos.originalIndex}-${pos.value}`}
                className={`absolute rounded-full ${hasTooltip ? "group z-10 hover:z-50" : "z-10"}`}
                // eslint-disable-next-line no-restricted-syntax -- runtime bee-swarm dot: computed left:${pct}% + measured px top/centering offsets + data-driven theme color; no closed token
                UNSAFE_style={baseDotStyle(
                  clampDotPosition(pos.x),
                  (compact ? 28 : 40) + pos.y,
                  isDark,
                  hasTooltip
                )}
              >
                {/* Hover tooltip - only rendered for dots with player names on web */}
                {hasTooltip && (
                  <Box
                    className={`absolute bottom-full left-1/2 mb-1 hidden rounded-none bg-surface-base shadow-hard-2 -translate-x-1/2 pointer-events-none group-hover:flex ${isDark ? "" : "border border-outline-subtle"}`}
                  >
                    <Box className="flex flex-col items-center px-3 py-1">
                      <Text
                        variant="caption"
                        className="whitespace-nowrap text-center text-content-secondary"
                      >
                        {pos.playerName}
                      </Text>
                      <Text
                        variant="title-sm"
                        className="text-center text-accent-primary"
                      >
                        {Math.round(pos.value)}
                      </Text>
                    </Box>
                    {/* Arrow */}
                    <Box
                      className="absolute left-1/2 top-full -translate-x-1/2"
                      // eslint-disable-next-line no-restricted-syntax -- CSS-triangle arrow (0×0 box, '5px solid transparent' border shorthands); not expressible in closed tokens
                      UNSAFE_style={tooltipArrowStyle(isDark)}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
          {/* Player dot - cyan dot rendered on top (z-index 10), hidden when no data */}
          {metric.value != null && (
            <Box
              className="absolute rounded-full"
              // eslint-disable-next-line no-restricted-syntax -- runtime marker dot: computed left:${pct}% + top:50% + negative-margin centering + fixed-constant fill color
              UNSAFE_style={markerDotStyle(
                PLAYER_DOT_COLOR,
                clampDotPosition((metric.value / maxValue) * 100)
              )}
            />
          )}
          {/* Comparison player dot - orange dot (when comparing to a specific player) */}
          {comparisonValue != null && (
            <Box
              className="absolute rounded-full"
              // eslint-disable-next-line no-restricted-syntax -- runtime marker dot: computed left:${pct}% + top:50% + negative-margin centering + fixed-constant fill color
              UNSAFE_style={markerDotStyle(
                COMPARISON_DOT_COLOR,
                clampDotPosition((comparisonValue / maxValue) * 100)
              )}
            />
          )}
        </Box>
      </Stack>
      {/* Axis labels - below the line */}
      <Stack direction="horizontal" className="relative h-4 px-1">
        <Text variant="caption" className="absolute left-0 text-content-muted">
          0
        </Text>
        <Text
          variant="caption"
          className="absolute left-1/2 text-content-muted -translate-x-1/2"
        >
          {Math.round(maxValue / 2)}
        </Text>
        <Text variant="caption" className="absolute right-0 text-content-muted">
          {maxValue}
        </Text>
      </Stack>
    </Stack>
  </Stack>
);

MetricDistributionRowView.displayName = "MetricDistributionRowView";

export default React.memo(MetricDistributionRowView);
